import React, { useState } from "react";

import GenericButton from "components/common/buttons/GenericButton";
import { useLocale } from "lib/hooks/useLocale";
import { AppliedCouponType } from "lib/contexts/CheckoutProvider";
import { classNames } from "lib/utils/helpers";
import { CreditCard, SealPercent, Truck } from "@phosphor-icons/react";
import { Icon } from "@shopflo/ui/icon";
import { CouponPageConfigType } from "lib/types/merchantConfigTypes";
import { hexToRgba } from "@shopflo/ui/utils";
import DotsLoader from "components/common/loaders/DotsLoader";

interface CouponCardProps {
  couponCode: string;
  couponHeader: string;
  couponTitle: string;
  couponDesc: string;
  moreInfo: string[];
  isApplicable: boolean;
  unApplicabilityReason: string;
  isStackable: boolean;
  totalDiscount: number;
  isAutoApplicable: boolean;
  onApplyClick: (couponCode: string, isStackable: boolean, couponTitle: string) => Promise<void>;
  nonRewardAppliedCoupons: AppliedCouponType[];
  couponType?: string;
  customisations?: CouponPageConfigType;
  isAchievable?: boolean;
}

const CouponIcons: Record<string, React.ElementType> = {
  SHIPPING: Truck,
  PAYMENT_OFFER: CreditCard,
};

const CouponCard: React.FC<CouponCardProps> = React.memo(
  ({
    couponCode,
    couponHeader,
    couponTitle,
    couponDesc,
    moreInfo,
    isApplicable,
    unApplicabilityReason,
    isStackable,
    totalDiscount,
    isAutoApplicable,
    nonRewardAppliedCoupons,
    onApplyClick,
    couponType,
    customisations,
    isAchievable,
  }) => {
    const [showMore, setShowMore] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const showUnavailableReasons = !isApplicable && !isStackable;
    const greyedOut = !isApplicable && !isStackable && !isAchievable;
    const { t } = useLocale();

    function getCouponIcon(
      couponType: string,
      greyedOut: boolean,
      primaryColor?: string,
      isAchievable?: boolean,
    ) {
      let IconComponent: typeof Truck | typeof CreditCard | typeof SealPercent = SealPercent;
      switch (couponType) {
        case "SHIPPING_OFFERS":
          IconComponent = Truck;
          break;
        case "PAYMENT_OFFERS":
          IconComponent = CreditCard;
          break;
        default:
          IconComponent = SealPercent;
      }
      return (
        <Icon
          IconComponent={IconComponent}
          size="sm"
          style={{
            color: isAchievable
              ? "var(--flo-gray-dark-color)"
              : greyedOut
              ? "var(--flo-gray-dark-color)"
              : primaryColor ?? undefined,
          }}
          iconProps={{
            weight:'fill'
          }}
        />
      );
    }

    return (
      <div
        className={classNames(
          "group relative box-border flex h-full w-full flex-col items-start overflow-hidden rounded-2xl border border-zinc-200 bg-white pb-3",
          greyedOut ? "opacity-60" : "",
        )}
        style={{
          borderRadius: customisations?.cardRadius,
        }}>
        <div
          className={classNames(
            "flex w-full flex-row items-center justify-between py-2 pl-3 pr-2",
            greyedOut ? "" : "",
          )}
          style={{
            background: greyedOut
              ? undefined
              : `linear-gradient(to bottom, ${hexToRgba(
                  customisations?.primaryColor ?? "#000000",
                  0.2,
                )}, #FFF)`,
          }}>
          <div className={`flex flex-row items-center gap-1 ${greyedOut ? "w-full" : "w-3/4"}`}>
            <div className="flex items-center justify-start">
              {getCouponIcon(
                couponType ?? "DEFAULT",
                greyedOut,
                customisations?.primaryColor ?? "#000000",
                isAchievable,
              )}
            </div>

            <h3 className="truncate pr-2 text-text-sm-semibold uppercase">
              {Boolean(couponTitle) ? couponTitle : couponCode}
            </h3>
          </div>

          {!greyedOut && (
            <button
              type="button"
              disabled={isLoading}
              className="flex min-h-[28px] min-w-[52px] items-center justify-center text-text-xs-semibold px-2.5 py-1 text-white"
              style={{
                backgroundColor: customisations?.primaryColor,
                borderRadius: customisations?.buttonRadius,
                color: customisations?.textColor,
              }}
              onClick={async () => {
                if (isLoading) return;
                if (isStackable) {
                  await onApplyClick(couponCode, isStackable, couponTitle);
                  return;
                }
                try {
                  setIsLoading(true);
                  await onApplyClick(couponCode, isStackable, couponTitle);
                } finally {
                  setIsLoading(false);
                }
              }}>
              {isLoading ? (
                <DotsLoader color={customisations?.textColor ?? "#FFFFFF"} />
              ) : isAchievable ? (
                t("add_items")
              ) : (
                t("apply")
              )}
            </button>
          )}
        </div>

        <div className="group relative flex w-full flex-col items-start justify-between gap-2 overflow-x-hidden px-3">
          <div className="flex flex-col">
            <div className="flex flex-col">
              <h3 className="text-text-xs-semibold" style={{ color: "var(--flo-coal-dark-color)" }}>
                {couponHeader}
              </h3>
              {couponDesc && Boolean(couponDesc.length) && (
                <p className="text-text-xs-medium" style={{ color: "var(--flo-coal-light-color)" }}>
                  {couponDesc}
                </p>
              )}
            </div>
          </div>

          {!greyedOut && totalDiscount > 0 && (
            <p className="text-xs font-medium text-yay-dark">
              {nonRewardAppliedCoupons?.filter((c) => !(c?.isFreebie && c?.autoApplied)).length > 0
                ? t("save_x_more", { price: totalDiscount })
                : t("save_x", { price: totalDiscount })}
            </p>
          )}

          {showUnavailableReasons && (
            <p className={classNames("text-xs font-medium ", isAchievable ? "text-yellow-600" : "text-ouch")}>
              {unApplicabilityReason}
            </p>
          )}

          {moreInfo &&
            moreInfo.length > 0 &&
            (showMore ? (
              <>
                <ul className="flex w-full flex-col space-y-1 px-2 py-3">
                  {moreInfo?.map((point: string, index: number) => (
                    <li
                      className="ml-4 list-disc text-xs font-normal text-coal-light"
                      key={`${couponCode}--${index}`}>
                      <p>{point}</p>
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  onClick={() => setShowMore(false)}
                  className="w-fit text-text-xxs-medium outline-none"
                  style={{ color: "var(--flo-coal-dark-color)" }}>
                  Hide details
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={() => setShowMore(true)}
                className="w-fit text-text-xxs-medium outline-none text-zinc-500">
                {t("more")}
              </button>
            ))}
        </div>
      </div>
    );
  },
);

export default CouponCard;
